package org.vreo.devicehub.features.core.data.protocolBuffer

import vreo.volt.protocol.*
import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.data.protocolBuffer.models.ProtoBufRequestType
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toVideoConfig
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toVideoConfigurationProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toAnprDevice
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toDeviceConfigurationProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toAnprCameraMessage
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toDeviceSettings
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toDeviceSettingsProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toRoiListProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toRois
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toSingleShotAutoFocusConfig
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toSingleShotAutoFocusProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toStaticImageProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toStaticImageRequest
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toVrmConfigurationListProto
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toVrmOutputConfigurations
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toWebRtcConfig
import org.vreo.devicehub.features.core.data.protocolBuffer.mappers.toWebRtcConfigProto
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoConfig
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.anprDeviceSettings.AnprDeviceSettings
import org.vreo.devicehub.features.core.domain.device.imageRequest.StaticImageRequest
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputConfig
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage
import org.vreo.devicehub.features.core.domain.device.webRtc.WebRtcOfferAnswer
import org.vreo.devicehub.features.core.domain.device.roi.Roi
import org.vreo.devicehub.features.core.domain.device.singleShootAutoFocus.SingleShotAutoFocusConfig

/**
 * Local models that can be RECEIVED from the device (decoded).
 */
sealed interface IncomingProtoBufMessage {
    data class PingPayload(val data: Ping) : IncomingProtoBufMessage
    data class AnprMessagePayload(val data: AnprMessage) : IncomingProtoBufMessage
    data class VideoConfigurationPayload(val data: VideoConfiguration) : IncomingProtoBufMessage
    data class VrmConfigurationPayload(val data: VrmConfigurationList) : IncomingProtoBufMessage
    data class DeviceConfigurationPayload(val data: DeviceConfiguration) : IncomingProtoBufMessage
    data class WebRtcAnswerPayload(val data: WebRtcAnswer) : IncomingProtoBufMessage
    data class RoiListPayload(val data: RoiList) : IncomingProtoBufMessage
    data class StaticImagePayload(val data: StaticImage) : IncomingProtoBufMessage
    data class DeviceSettingsMessage(val data: DeviceSettings) : IncomingProtoBufMessage
    data class SingleShotAutofocusMessage(val data: SingleShotAutofocus) : IncomingProtoBufMessage
}

/**
 * Local models that can be SENT to the device (encoded).
 */
sealed interface OutgoingProtoBufMessage {
    data class PingMessage(val data: Int) : OutgoingProtoBufMessage
    data class AnprMessageMessage(val data: AnprCameraMessage) : OutgoingProtoBufMessage
    data class VideoConfigurationMessage(val data: VideoConfig) : OutgoingProtoBufMessage
    data class VrmConfigurationMessage(val data: List<VrmOutputConfig>) : OutgoingProtoBufMessage
    data class DeviceConfigurationMessage(val data: AnprDevice) : OutgoingProtoBufMessage
    data class WebRtcAnswerMessage(val data: WebRtcOfferAnswer) : OutgoingProtoBufMessage
    data class RoiListMessage(val data: List<Roi>) : OutgoingProtoBufMessage
    data class StaticImageMessage(val data: StaticImageRequest) : OutgoingProtoBufMessage
    data class DeviceSettingsMessage(val data: AnprDeviceSettings) : OutgoingProtoBufMessage
    data class SingleShotAutofocusMessage(val data: SingleShotAutoFocusConfig) : OutgoingProtoBufMessage
}

/**
 * Convert local data model to protocol buffer message
 * encoded bytes.
 *
 * @param request type of protocol buffer message, GET or POST
 * @param message local data model to be converted included in [OutgoingProtoBufMessage]
 * @param id message id, defaults to -1
 * @return Result of byte array on success or error on failure
 */
fun localModelToProtoBufMessage(
    request: ProtoBufRequestType,
    message: OutgoingProtoBufMessage,
    id: Int = -1
): Result<ByteArray, DataError.Local> {
    // Convert local data model to protobuf message
    val payload = try {
        when (message) {
            is OutgoingProtoBufMessage.AnprMessageMessage -> {
                throw IllegalArgumentException(
                    "Anpr message from local to protobuf is not supported"
                )
            }

            is OutgoingProtoBufMessage.PingMessage -> {
                IncomingProtoBufMessage.PingPayload(Ping(message.data))
            }

            is OutgoingProtoBufMessage.VideoConfigurationMessage -> {
                IncomingProtoBufMessage.VideoConfigurationPayload(
                    message.data.toVideoConfigurationProto()
                )
            }

            is OutgoingProtoBufMessage.VrmConfigurationMessage -> {
                IncomingProtoBufMessage.VrmConfigurationPayload(
                    message.data.toVrmConfigurationListProto()
                )
            }

            is OutgoingProtoBufMessage.DeviceConfigurationMessage -> {
                IncomingProtoBufMessage.DeviceConfigurationPayload(
                    message.data.toDeviceConfigurationProto()
                )
            }

            is OutgoingProtoBufMessage.WebRtcAnswerMessage -> {
                IncomingProtoBufMessage.WebRtcAnswerPayload(
                    message.data.toWebRtcConfigProto()
                )
            }

            is OutgoingProtoBufMessage.RoiListMessage -> {
                IncomingProtoBufMessage.RoiListPayload(
                    message.data.toRoiListProto()
                )
            }

            is OutgoingProtoBufMessage.StaticImageMessage -> {
                IncomingProtoBufMessage.StaticImagePayload(
                    message.data.toStaticImageProto()
                )
            }

            is OutgoingProtoBufMessage.DeviceSettingsMessage -> {
                IncomingProtoBufMessage.DeviceSettingsMessage(
                    message.data.toDeviceSettingsProto()
                )
            }

            is OutgoingProtoBufMessage.SingleShotAutofocusMessage -> {
                IncomingProtoBufMessage.SingleShotAutofocusMessage(
                    message.data.toSingleShotAutoFocusProto()
                )
            }
        }
    } catch (e: Exception) {
        AppLogger.error("Failed to convert local model to protobuf, error: ${e.message}")
        return Result.Error(DataError.Local.UNKNOWN)
    }

    return try {
        Result.Success(
            protobufEncodeMessage(
                id = id,
                request = if (request == ProtoBufRequestType.GET) {
                    RequestType.GET
                } else {
                    RequestType.POST
                },
                payload = payload
            )
        )
    } catch (e: Exception) {
        AppLogger.error(
            "Failed to encode protobuf message to bytes, error: ${e.message}"
        )
        Result.Error(DataError.Local.UNKNOWN)
    }
}

/**
 * Convert protocol buffer message to local data model
 *
 * We assume that DeviceHub (UI) will always behave as client and therefore message type (POS/GET)
 * is always ignored as this method should only be used to decode requested (returned) data.
 *
 * @param byteArray row protocol buffer message to be converted
 * @return Result of local model on success or error on failure
 */
fun protoBufMessageToLocalModel(
    byteArray: ByteArray
): Result<OutgoingProtoBufMessage, DataError.Local> {
    // Decode message
    val message = try {
        protobufGetMessagePayloadFromBytes(byteArray)
    } catch (e: Exception) {
        AppLogger.error("Failed to decode protobuf message, error: ${e.message}")
        return Result.Error(DataError.Local.INVALID_ARGUMENT)
    }

    // Convert to local model
    val result = try {
        when (message) {
            is IncomingProtoBufMessage.PingPayload -> {
                OutgoingProtoBufMessage.PingMessage(message.data.id)
            }

            is IncomingProtoBufMessage.AnprMessagePayload -> {
                OutgoingProtoBufMessage.AnprMessageMessage(
                    message.data.toAnprCameraMessage()
                )
            }

            is IncomingProtoBufMessage.VideoConfigurationPayload -> {
                OutgoingProtoBufMessage.VideoConfigurationMessage(
                    message.data.toVideoConfig()
                )
            }

            is IncomingProtoBufMessage.VrmConfigurationPayload -> {
                OutgoingProtoBufMessage.VrmConfigurationMessage(
                    message.data.toVrmOutputConfigurations()
                )
            }

            is IncomingProtoBufMessage.DeviceConfigurationPayload -> {
                OutgoingProtoBufMessage.DeviceConfigurationMessage(
                    message.data.toAnprDevice()
                )
            }

            is IncomingProtoBufMessage.WebRtcAnswerPayload -> {
                OutgoingProtoBufMessage.WebRtcAnswerMessage(
                    message.data.toWebRtcConfig()
                )
            }

            is IncomingProtoBufMessage.RoiListPayload -> {
                OutgoingProtoBufMessage.RoiListMessage(
                    message.data.toRois()
                )
            }

            is IncomingProtoBufMessage.StaticImagePayload -> {
                OutgoingProtoBufMessage.StaticImageMessage(
                    message.data.toStaticImageRequest()
                )
            }

            is IncomingProtoBufMessage.DeviceSettingsMessage -> {
                OutgoingProtoBufMessage.DeviceSettingsMessage(
                    message.data.toDeviceSettings()
                )
            }

            is IncomingProtoBufMessage.SingleShotAutofocusMessage -> {
                OutgoingProtoBufMessage.SingleShotAutofocusMessage(
                    message.data.toSingleShotAutoFocusConfig()
                )
            }
        }
    } catch (e: Exception) {
        AppLogger.error(
            "Failed to convert protobuf message to local model, error: ${e.message}"
        )
        return Result.Error(DataError.Local.UNKNOWN)
    }

    return Result.Success(result)
}

/**
 * Encodes a message to a byte array.
 *
 * @param id The ID of the message.
 * @param request The type of the message.
 * @param payload The payload of the message.
 * @return The byte array of the encoded message.
 */
private fun protobufEncodeMessage(
    id: Int,
    request: RequestType,
    payload: IncomingProtoBufMessage
): ByteArray {
    val baseWrapper = MessageWrapper(id = id, request_type = request)
    val result = when (payload) {
        is IncomingProtoBufMessage.PingPayload -> baseWrapper
            .copy(ping = payload.data)

        is IncomingProtoBufMessage.VideoConfigurationPayload -> baseWrapper
            .copy(video_configuration = payload.data)

        is IncomingProtoBufMessage.AnprMessagePayload -> baseWrapper
            .copy(anpr_message = payload.data)

        is IncomingProtoBufMessage.DeviceConfigurationPayload -> baseWrapper
            .copy(device_configuration = payload.data)

        is IncomingProtoBufMessage.VrmConfigurationPayload -> baseWrapper
            .copy(vrms = payload.data)

        is IncomingProtoBufMessage.WebRtcAnswerPayload -> baseWrapper
            .copy(webrtc_answer = payload.data)

        is IncomingProtoBufMessage.RoiListPayload -> baseWrapper
            .copy(rois = payload.data)

        is IncomingProtoBufMessage.StaticImagePayload -> baseWrapper
            .copy(get_image = payload.data)

        is IncomingProtoBufMessage.DeviceSettingsMessage -> baseWrapper
            .copy(settings = payload.data)

        is IncomingProtoBufMessage.SingleShotAutofocusMessage -> baseWrapper
            .copy(single_shot_autofocus = payload.data)
    }
    return MessageWrapper.ADAPTER.encode(result)
}

/**
 * Gets the payload of a message from a byte array.
 *
 * @param byteArray The byte array to get the payload of.
 * @return The payload of the message.
 */
private fun protobufGetMessagePayloadFromBytes(byteArray: ByteArray): IncomingProtoBufMessage {
    val message = protobufDecodeMessage(byteArray)
    return protobufGetMessagePayload(message)
}

/**
 * Decodes a message from a byte array.
 * @param byteArray The byte array to decode.
 */
private fun protobufDecodeMessage(byteArray: ByteArray): MessageWrapper {
    return MessageWrapper.ADAPTER.decode(byteArray)
}

/**
 * Gets the payload of a message.
 *
 * @param message The message to get the payload of.
 * @return The payload of the message.
 */
private fun protobufGetMessagePayload(message: MessageWrapper): IncomingProtoBufMessage {
    return when {
        message.ping != null -> IncomingProtoBufMessage.PingPayload(message.ping)

        message.video_configuration != null -> IncomingProtoBufMessage.VideoConfigurationPayload(
            message.video_configuration
        )

        message.anpr_message != null -> IncomingProtoBufMessage.AnprMessagePayload(
            message.anpr_message
        )

        message.device_configuration != null -> IncomingProtoBufMessage.DeviceConfigurationPayload(
            message.device_configuration
        )

        message.vrms != null -> IncomingProtoBufMessage.VrmConfigurationPayload(
            message.vrms
        )

        message.webrtc_answer != null -> IncomingProtoBufMessage.WebRtcAnswerPayload(
            message.webrtc_answer
        )

        message.rois != null -> IncomingProtoBufMessage.RoiListPayload(
            message.rois
        )

        message.get_image != null -> IncomingProtoBufMessage.StaticImagePayload(
            message.get_image
        )

        message.settings != null -> IncomingProtoBufMessage.DeviceSettingsMessage(
            message.settings
        )

        message.single_shot_autofocus != null -> IncomingProtoBufMessage.SingleShotAutofocusMessage(
            message.single_shot_autofocus
        )

        else -> throw IllegalArgumentException("Unknown message type or no payload set.")
    }
}
